import { adminIntroductionModel, changeServerLevelModel, characterModel, chatMessageModel, endCountDownMessageModel, introductionModel, levelVotesMessageModel, levelWonModel, selectHatMessageModel, simpleMessageModel, startLoadLevelModel } from "./schemas"
import {
	Settings
} from '../Settings'
import { game } from "../Game";

const RAD2DEG = 57.29577951308232;
const DEG2RAD = 0.017453292519943296;

const BODY_PARTS = {
	HEAD: 'head',
	BODY: 'body',
	THIGH_LEFT: 'thigh_left',
	THIGH_RIGHT: 'thigh_right',
	LEG_LEFT: 'leg_left',
	LEG_RIGHT: 'leg_right',
	FEET_LEFT: 'feet_left',
	FEET_RIGHT: 'feet_right',
	SHOULDER_LEFT: 'shoulder_left',
	SHOULDER_RIGHT: 'shoulder_right',
	ARM_LEFT: 'arm_left',
	ARM_RIGHT: 'arm_right',
	HAND_LEFT: 'hand_left',
	HAND_RIGHT: 'hand_right',
	BELLY: 'belly',
}

let lastValidMainData = {x: 0, y: 0};
let lastCharacter = null;
let lastTeleportRevision = 0;
let poseEpoch = 0;

const serializeAngle = radians => {
	let deg = (radians * RAD2DEG) % 360;
	if(deg<0) deg += 360;
	return Math.round((deg / 360) * 255);
}

const fixAngle = uint => {
	return Math.round((uint / 255) * 360);
}

const extractPosition = (body, relative = false) => {
	if(!body){
		return {
			x: Settings.destroyedPosition,
			y: Settings.destroyedPosition,
			r: 255,
		}
	}

	const position = body.GetPosition();
	// The torso origin was sampled once at the start of this packet. Reuse it
	// for every limb and vehicle instead of repeating the same WASM body read.
	return {
		x: position.x * Settings.PTM - (relative ? lastValidMainData.x : 0),
		y: position.y * Settings.PTM - (relative ? lastValidMainData.y : 0),
		r: serializeAngle(body.GetAngle()),
	};
}

// id 0 - 255
export const characterToBuffer = (characterClass, id, time = performance.now()) => {
	const lookup = characterClass.lookupObject;
	const teleportRevision = characterClass.multiplayerTeleportRevision || 0;
	if (lastCharacter !== characterClass || lastTeleportRevision !== teleportRevision) {
		poseEpoch = (poseEpoch + 1) % 10;
		lastCharacter = characterClass;
		lastTeleportRevision = teleportRevision;
	}
	characterClass.multiplayerPoseEpoch = poseEpoch;
	if (lookup.body) {
		const origin = lookup.body.GetPosition();
		lastValidMainData = {x: origin.x * Settings.PTM, y: origin.y * Settings.PTM};
	}


	// build expression uint
	// 2 5 5 values ( eyes (3), expression (6), empty (6))
	let eyesBit = 0;

	const eyeLeftCache = lookup.eye_left?.myTexture?.originalSprite?.texture?.label;
	const eyeRightCache = lookup.eye_right?.myTexture?.originalSprite?.texture?.label;
	if(eyeLeftCache?.includes('Closed') || eyeRightCache?.includes('Closed')){
		eyesBit = 100;
	}
	let expressionBit = 0;
	if(characterClass.expression === 'Pain') expressionBit = 10;
	if(characterClass.expression === 'Special') expressionBit = 20;
	// The unused units digit marks respawns/portal transfers without changing
	// the binary schema. Older clients already ignore this digit for expressions.
	const expression = eyesBit + expressionBit + poseEpoch;
	//

	const characterData = {
		id,
		time: Math.floor(time),
		mirror: +characterClass.flipped,
		expression,
		main: [extractPosition(lookup[BODY_PARTS.BODY])],
		parts:[
			extractPosition(lookup[BODY_PARTS.HEAD], true),
			extractPosition(lookup[BODY_PARTS.SHOULDER_LEFT], true),
			extractPosition(lookup[BODY_PARTS.SHOULDER_RIGHT], true),
			extractPosition(lookup[BODY_PARTS.ARM_LEFT], true),
			extractPosition(lookup[BODY_PARTS.ARM_RIGHT], true),
			extractPosition(lookup[BODY_PARTS.HAND_LEFT], true),
			extractPosition(lookup[BODY_PARTS.HAND_RIGHT], true),
			extractPosition(lookup[BODY_PARTS.BELLY], true),
			extractPosition(lookup[BODY_PARTS.THIGH_LEFT], true),
			extractPosition(lookup[BODY_PARTS.THIGH_RIGHT], true),
			extractPosition(lookup[BODY_PARTS.LEG_LEFT], true),
			extractPosition(lookup[BODY_PARTS.LEG_RIGHT], true),
			extractPosition(lookup[BODY_PARTS.FEET_LEFT], true),
			extractPosition(lookup[BODY_PARTS.FEET_RIGHT], true),
		],
		vehicleParts:[],
	}


	if(game.vehicle){
		if(game.selectedVehicle === 1){ // BIKE
			const vehicleLookup = game.vehicle.lookupObject;
			characterData.vehicleParts = [
				extractPosition(vehicleLookup.frame, true),
				extractPosition(vehicleLookup.wheel_back, true),
				extractPosition(vehicleLookup.wheel_front, true),
				extractPosition(vehicleLookup.pedal, true),
			];
		} else if(game.selectedVehicle === 2){ // DIRTBIKE
			const vehicleLookup = game.vehicle.lookupObject;
			characterData.vehicleParts = [
				extractPosition(vehicleLookup.frame, true),
				extractPosition(vehicleLookup.wheel_back, true),
				extractPosition(vehicleLookup.wheel_front, true),
				extractPosition(vehicleLookup.front_axis, true),
				extractPosition(vehicleLookup.back_axis, true),
			];
		} else if(game.selectedVehicle === 3 || game.selectedVehicle === 7){ // NOVEHICLE, FREERUNNER
			characterData.vehicleParts = [
				extractPosition(null),
			];
		} else if(game.selectedVehicle === 4){
			const vehicleLookup = game.vehicle.lookupObject;
			characterData.vehicleParts = [
				extractPosition(vehicleLookup.frame, true),
				extractPosition(vehicleLookup.wheel_back, true),
				extractPosition(vehicleLookup.wheel_front, true),
			];
		} else if(game.selectedVehicle === 5){ // SKIPPYBALL
			const vehicleLookup = game.vehicle.lookupObject;

			characterData.vehicleParts = [
				extractPosition(vehicleLookup.frame, true),
			];

			const steps = 12;
			for(let i = 1; i<= steps; i+=1){
				const bouncyBall = vehicleLookup['b'+i];
				characterData.vehicleParts.push(extractPosition(bouncyBall, true));
			}
		} else if(game.selectedVehicle === 6){ // FODDYCAN
			const vehicleLookup = game.vehicle.lookupObject;
			characterData.vehicleParts = [
				extractPosition(vehicleLookup.frame, true),
				extractPosition(vehicleLookup.hammer, true),
			];
		}
	}

	if(game.cameraFocusObject && game.cameraFocusObject !== game.cameraFocusCharacterObject && !game.cameraFocusObject.destroyed && !game.cameraFocusObject.isMultiplayerCamera){
		characterData.main.push(extractPosition(game.cameraFocusObject));
	}

	const buffer = characterModel.toBuffer(characterData);
	return buffer;
}

export const characterFromBuffer = buffer => {
	const characterData = characterModel.fromBuffer(buffer);
	characterData.main ||= [];
	characterData.parts ||= [];
	characterData.vehicleParts ||= [];
	const decode = p => {
		// Integer fields truncate the historical 133.7 destruction sentinel.
		if (p.x === Math.trunc(Settings.destroyedPosition) && p.y === Math.trunc(Settings.destroyedPosition) && p.r === 255) {
			p.x = p.y = Settings.destroyedPosition;
		}
		p.r = fixAngle(p.r);
	};
	characterData.main.forEach(decode);
	characterData.parts.forEach( p => {
		decode(p);
	});
	characterData.vehicleParts.forEach( p => {
		decode(p);
	});
	return characterData;
}

// ADMIN INTRODUCTION
export const dataToAdminIntroductionBuffer = (name, levelID, lobbyState, selectedVehicle) => {
	const buffer = adminIntroductionModel.toBuffer({name, levelID, lobbyState, selectedVehicle});
	return buffer;
}

export const dataFromAdminIntroductionBuffer = buffer => {
	const introductionData = adminIntroductionModel.fromBuffer(buffer);
	introductionData.name = introductionData.name.trim();
	introductionData.levelID = introductionData.levelID.trim();
	introductionData.lobbyState = introductionData.lobbyState;
	introductionData.selectedVehicle = introductionData.selectedVehicle;
	return introductionData;
}

// PEER INTRODUCTION
export const dataToIntroductionBuffer = (name, lobbyState, selectedVehicle) => {
	const buffer = introductionModel.toBuffer({name, lobbyState, selectedVehicle})
	return buffer;
}

export const dataFromIntroductionBuffer = buffer => {
	const introductionData = introductionModel.fromBuffer(buffer);
	introductionData.name = introductionData.name.trim();
	introductionData.lobbyState = introductionData.lobbyState;
	introductionData.selectedVehicle = introductionData.selectedVehicle;
	return introductionData;
}

// SIMPLE MESSAGE
export const dataToSimpleMessageBuffer = type => {
	const buffer = simpleMessageModel.toBuffer({type});
	return buffer;
}

export const dataFromSimpleMessageBuffer = buffer => {
	const simpleMessageData = simpleMessageModel.fromBuffer(buffer);
	return simpleMessageData;
}

// START LOAD LEVEL
export const dataToStartLoadLevelBuffer = levelID => {
	const buffer = startLoadLevelModel.toBuffer({levelID});
	return buffer;
}

export const dataFromStartLoadLevelBuffer = buffer => {
	const simpleMessageData = startLoadLevelModel.fromBuffer(buffer);
	return simpleMessageData;
}

// CHANGE LEVEL
export const dataToChangeServerLevelBuffer = levelID => {
	const buffer = changeServerLevelModel.toBuffer({levelID});
	return buffer;
}

export const dataFromChangeServerLevelBuffer = buffer => {
	const simpleMessageData = changeServerLevelModel.fromBuffer(buffer);
	return simpleMessageData;
}

// LEVEL WON
export const dataToLevelWonBuffer = time => {
	const buffer = levelWonModel.toBuffer({time});
	return buffer;
}

export const dataFromLevelWonBuffer = buffer => {
	const simpleMessageData = levelWonModel.fromBuffer(buffer);
	return simpleMessageData;
}

// CHAT MESSAGE
export const dataToChatMessageBuffer = message => {
	const slicedMessage = message.substr(0, Settings.maxChatLength);
	const buffer = chatMessageModel.toBuffer({message:slicedMessage});
	return buffer;
}

export const dataFromChatMessageBuffer = buffer => {
	const chatMessageData = chatMessageModel.fromBuffer(buffer);
	chatMessageData.message = chatMessageData.message.substr(0, Settings.maxChatLength).trim();
	return chatMessageData;
}

// END COUNTDOWN MESSAGE
export const dataToEndCountDownMessageBuffer = (levelIds, timeOffset) => {
	const buffer = endCountDownMessageModel.toBuffer({level1:levelIds[0], level2:levelIds[1], level3:levelIds[2], timeOffset});
	return buffer;
}

export const dataFromEndCountDownMessageBuffer = buffer => {
	const endCountDownMessageData = endCountDownMessageModel.fromBuffer(buffer);
	endCountDownMessageData.levelIds = [endCountDownMessageData.level1, endCountDownMessageData.level2, endCountDownMessageData.level3];
	endCountDownMessageData.timeOffset = endCountDownMessageData.timeOffset;
	return endCountDownMessageData;
}

// LEVEL VOTES MESSAGE
export const dataToLevelVotesMessageBuffer = votes => {
	const buffer = levelVotesMessageModel.toBuffer({level1:votes[0], level2:votes[1], level3:votes[2], level4:votes[3]});
	return buffer;
}

export const dataFromLevelVotesMessageBuffer = buffer => {
	const levelVotesMessageData = levelVotesMessageModel.fromBuffer(buffer);
	levelVotesMessageData.votes = [levelVotesMessageData.level1, levelVotesMessageData.level2, levelVotesMessageData.level3, levelVotesMessageData.level4];
	return levelVotesMessageData;
}


// SELECT HAT MESSAGE
export const dataToSelectHatMessageBuffer = (hat, hatOffsetLength, hatOffsetAngle) => {
	hatOffsetAngle = serializeAngle(hatOffsetAngle);
	const buffer = selectHatMessageModel.toBuffer({hat, hatOffsetLength, hatOffsetAngle});
	return buffer;
}

export const dataFromSelectHatMessageBuffer = buffer => {
	const selectHatMessageData = selectHatMessageModel.fromBuffer(buffer);
	selectHatMessageData.hatOffsetAngle = fixAngle(selectHatMessageData.hatOffsetAngle) * DEG2RAD;
	return selectHatMessageData;
}
